import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/sonner";
import { Loader2, Trash2, Upload, Plus } from "lucide-react";
import { photoSrc } from "@/components/funnel/SuccessStories";

const errMsg = (err, fallback) => (typeof err.response?.data?.detail === "string" ? err.response.data.detail : fallback);

const AddPartner = ({ t, onAdded }) => {
  const [f, setF] = useState({ name: "", website: "", logo_url: "" });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/partner-logos/upload", fd);
      setF((x) => ({ ...x, logo_url: data.logo_url }));
    } catch (err) {
      toast.error(errMsg(err, t("funnel.common.error")));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  const save = async (e) => {
    e.preventDefault();
    if (!f.logo_url) return toast.error(t("partners.needLogo"));
    setBusy(true);
    try {
      await api.post("/admin/partner-logos", { ...f, published: true });
      toast.success(t("partners.added"));
      setF({ name: "", website: "", logo_url: "" });
      onAdded();
    } catch (err) {
      toast.error(errMsg(err, t("funnel.common.error")));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card className="border-slate-200 p-5" data-testid="partner-add-form">
      <form onSubmit={save} className="grid gap-3 sm:grid-cols-[auto_1fr_1fr_auto] sm:items-end">
        <div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="partner-logo-input" />
          <button type="button" onClick={() => fileRef.current?.click()} className="flex h-16 w-28 items-center justify-center rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 text-xs font-semibold text-slate-500 hover:border-amber-400" data-testid="partner-logo-upload">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : f.logo_url ? <img src={photoSrc(f.logo_url)} alt="" className="max-h-12 max-w-[96px] object-contain" /> : <><Upload className="mr-1 h-4 w-4" /> {t("partners.logo")}</>}
          </button>
        </div>
        <div><Label htmlFor="p-name">{t("partners.name")}</Label><Input id="p-name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1" data-testid="partner-name" /></div>
        <div><Label htmlFor="p-site">{t("partners.website")}</Label><Input id="p-site" placeholder="https://" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} className="mt-1" data-testid="partner-website" /></div>
        <Button type="submit" disabled={busy} className="bg-amber-600 hover:bg-amber-700" data-testid="partner-save"><Plus className="mr-1.5 h-4 w-4" /> {t("partners.add")}</Button>
      </form>
    </Card>
  );
};

const Tile = ({ name, logo, badge, checked, onToggle, onDelete, testId }) => (
  <Card className="flex items-center gap-3 border-slate-200 p-3" data-testid={testId}>
    <span className="flex h-14 w-24 shrink-0 items-center justify-center rounded-lg border border-slate-100 bg-white"><img src={photoSrc(logo)} alt={name} className="max-h-10 max-w-[84px] object-contain" /></span>
    <div className="min-w-0 flex-1">
      <p className="truncate font-semibold text-slate-900">{name}</p>
      <Badge variant="outline" className="mt-1">{badge}</Badge>
    </div>
    <Switch checked={checked} onCheckedChange={onToggle} data-testid={`${testId}-toggle`} />
    {onDelete && <Button size="icon" variant="ghost" onClick={onDelete} data-testid={`${testId}-delete`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>}
  </Card>
);

export const PartnersManager = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const load = () => api.get("/admin/partner-logos").then(({ data: d }) => setData(d)).catch(() => setData({ partners: [], employers: [] }));
  useEffect(() => { load(); }, []);
  const run = (p) => p.then(load).catch((err) => toast.error(errMsg(err, t("funnel.common.error"))));
  const togglePartner = (p, v) => run(api.put(`/admin/partner-logos/${p.partner_id}`, { name: p.name, logo_url: p.logo_url, website: p.website || "", published: v }));
  const removePartner = (p) => window.confirm(t("partners.deleteConfirm")) && run(api.delete(`/admin/partner-logos/${p.partner_id}`));
  const toggleEmployer = (e, v) => run(api.patch(`/admin/employer-wall/${e.id}`, { hidden: !v }));
  if (!data) return <Loader2 className="mx-auto h-6 w-6 animate-spin text-amber-600" />;
  const empty = !data.partners.length && !data.employers.length;
  return (
    <div className="space-y-4" data-testid="partners-manager">
      <p className="text-sm text-slate-500">{t("partners.help")}</p>
      <AddPartner t={t} onAdded={load} />
      {empty && <p className="py-6 text-center text-slate-500" data-testid="partners-empty">{t("partners.empty")}</p>}
      <div className="grid gap-3 md:grid-cols-2">
        {data.partners.map((p) => (
          <Tile key={p.partner_id} name={p.name} logo={p.logo_url} badge={t("partners.addedByYou")} checked={p.published}
            onToggle={(v) => togglePartner(p, v)} onDelete={() => removePartner(p)} testId="partner-row" />
        ))}
        {data.employers.map((e) => (
          <Tile key={e.id} name={e.name} logo={e.logo_url} badge={t("partners.fromEmployer")} checked={!e.hidden}
            onToggle={(v) => toggleEmployer(e, v)} testId="employer-wall-row" />
        ))}
      </div>
    </div>
  );
};
